import type { AxiosError } from 'axios';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { errorToString } from '~~/helpers/error/errorToString';
import { ALLOWED_METHODS } from '~~/types/expondoMiddleware';
import type { AllowedMethod } from '~~/types/expondoMiddleware';
import type { ApiResponse } from '~~/types/commercetools';
import { catchApiErrors } from '~/utils/ct/composables/helpers/apiHelpers';
import { useRequestHeaders } from '#imports';

export const useExpondoMiddleware = () => {

  const apiFetch = useApiFetch();
  const ssrHeaders = import.meta.server
    ? useRequestHeaders(['cookie', 'host', 'user-agent'])
    : undefined;
    
  const makeMiddlewareRequest = async <T>({
    endpoint,
    method,
    data,
    params,
    catchCallback,
  }: {
    endpoint: string;
    method: AllowedMethod;
    data?: unknown;
    params?: Record<string, unknown>;
    catchCallback?: (error: AxiosError) => void;
  }): Promise<T | null> => {

    try {

      const response = await apiFetch(EXPONDO_MIDDLEWARE_ROUTES.ROOT + endpoint, {
        method,
        params,
        body: data,
        credentials: 'same-origin',
        headers: {
          ...ssrHeaders,
          'Content-Type': 'application/json',
        },
      });

      return response;
    
    } catch (error: unknown) {

      if (catchCallback) {

        catchCallback(error as AxiosError);

        return null;
      
      } else {

        throw new Error(`An error occurred during request to ${endpoint}, ${errorToString(error)}`);
      
      }
    
    }
  
  };

  const makeCommercetoolsRequest = async <T> ({
    endpoint,
    data,
  }: {
    endpoint: string;
    data?: unknown;
  }): Promise<ApiResponse<T> | null> => {

    const responseData = await makeMiddlewareRequest<ApiResponse<T>>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.ROOT + endpoint,
      method: ALLOWED_METHODS.post,
      data,
    });

    if (responseData) {

      catchApiErrors(responseData);

    }

    return responseData || null;
  
  };

  const makeAdyenRequest = async <T> ({
    endpoint,
    data,
  }: {
    endpoint: string;
    data?: unknown;
  }): Promise<T | null> => {

    const responseData = await makeMiddlewareRequest<T>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.ADYEN.ROOT + endpoint,
      method: ALLOWED_METHODS.post,
      data,
    });

    return responseData || null;
  
  };

  return {
    makeMiddlewareRequest,
    makeCommercetoolsRequest,
    makeAdyenRequest,
  };

};